import { useState, useEffect } from "react";
import axios from "axios";
import { Reveal } from "./Reveal";
import { Star } from "./Logo";
import { Check, Loader2, Tablet, Smartphone, Tv, Sparkles, Shirt, Frame, Layers, ShoppingBag, Store, QrCode, Share2, ScanBarcode, ShieldCheck } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { metaTrack } from "@/lib/metaPixel";
import { gaEvent } from "@/lib/googleAnalytics";

const MIRROR_ICONS = [Sparkles, Shirt, Frame, Layers, ShoppingBag, Store, QrCode, Share2, ScanBarcode];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const B2B_IMG = "https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

const inputCls = "w-full rounded-2xl border px-5 py-3.5 text-sm outline-none transition-colors duration-300 focus:border-[var(--camel)] bg-white";

const HW_ICONS = [Tablet, Smartphone, Tv];
const HW_IMAGES = [
  "https://static.prod-images.emergentagent.com/jobs/78bcda24-ecc7-4097-99a8-74d5dca5b1b3/images/c06b8f6d2fe4f55909f28ae3b3da895336a797632687769306828b216454e31c.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/78bcda24-ecc7-4097-99a8-74d5dca5b1b3/images/69b9ad439fa87d71ef7bf08964ab9a2e4c18f6641454b5288831616e87a17fb6.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/78bcda24-ecc7-4097-99a8-74d5dca5b1b3/images/2d52703918b85c2b07919d9f6c9a696007f4c11cdbdd6ab03abbf5d045fb778e.jpeg",
];

export default function B2B({ selectedPlan }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", boutique: "", email: "", message: "", plan: "" });
  const [status, setStatus] = useState("idle");
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    if (selectedPlan) setForm((f) => ({ ...f, plan: selectedPlan }));
  }, [selectedPlan]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      let utm = {};
      try { utm = JSON.parse(sessionStorage.getItem("mira-utm") || "{}"); } catch { utm = {}; }
      await axios.post(`${API}/leads`, { ...form, plan: form.plan || null, lang, utm_source: utm.source || null, utm_campaign: utm.campaign || null });
      metaTrack("Lead");
      gaEvent("generate_lead", { source: "b2b_form" });
      setStatus("success");
      setFeedback(t.b2b.form.success);
      setForm({ name: "", boutique: "", email: "", message: "", plan: "" });
    } catch (err) {
      setStatus("error");
      setFeedback(err?.response?.status === 429 ? t.b2b.form.rateLimited : t.b2b.form.error);
    }
  };

  return (
    <section id="boutiquen" className="py-24 sm:py-36" style={{ background: "var(--surface-secondary)" }} data-testid="b2b-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <p className="flex items-center gap-2 text-xs uppercase font-medium" style={{ color: "var(--camel)", letterSpacing: "0.16em" }}>
            <Star size={11} /> {t.b2b.kicker}
          </p>
          <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl font-semibold leading-tight" data-testid="b2b-title">
            {t.b2b.title}
          </h2>
          <p className="mt-5 max-w-md text-base md:text-lg leading-relaxed" style={{ color: "var(--muted-warm)" }}>
            {t.b2b.sub}
          </p>
          <ul className="mt-9 space-y-4">
            {t.b2b.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-sm sm:text-base" data-testid={`b2b-bullet-${i + 1}`}>
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full" style={{ background: "var(--rose-soft)" }}>
                  <Check size={13} style={{ color: "var(--camel)" }} />
                </span>
                <span style={{ color: "var(--on-surface)" }}>{b}</span>
              </li>
            ))}
          </ul>
          <div className="mt-10 overflow-hidden rounded-3xl border hidden lg:block" style={{ borderColor: "var(--line)" }}>
            <img src={B2B_IMG} alt="Boutique" className="h-56 w-full object-cover transition-transform duration-700 hover:scale-105" />
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            onSubmit={submit}
            className="rounded-3xl border p-8 sm:p-10 shadow-sm"
            style={{ background: "var(--surface)", borderColor: "var(--line)" }}
            data-testid="b2b-form"
          >
            <h3 className="font-serif-display text-2xl font-semibold">{t.b2b.form.title}</h3>
            <div className="mt-7 space-y-4">
              <input data-testid="b2b-input-name" required minLength={2} className={inputCls} style={{ borderColor: "var(--line)" }} placeholder={t.b2b.form.name} value={form.name} onChange={set("name")} />
              <input data-testid="b2b-input-boutique" required minLength={2} className={inputCls} style={{ borderColor: "var(--line)" }} placeholder={t.b2b.form.boutique} value={form.boutique} onChange={set("boutique")} />
              <input data-testid="b2b-input-email" required type="email" className={inputCls} style={{ borderColor: "var(--line)" }} placeholder={t.b2b.form.email} value={form.email} onChange={set("email")} />
              <div>
                <label className="mb-1.5 block text-xs font-medium uppercase" style={{ color: "var(--muted-warm)", letterSpacing: "0.1em" }}>
                  {t.b2b.form.plan}
                </label>
                <select data-testid="b2b-input-plan" className={inputCls} style={{ borderColor: "var(--line)" }} value={form.plan} onChange={set("plan")}>
                  <option value="">{t.b2b.form.planNone}</option>
                  <option value="Mira für Boutiquen">Mira für Boutiquen (CHF 149/Mt.)</option>
                  <option value="Ketten & Filialen">Ketten & Filialen (auf Anfrage)</option>
                </select>
              </div>
              <textarea data-testid="b2b-input-message" required minLength={5} rows={4} className={inputCls} style={{ borderColor: "var(--line)", resize: "none" }} placeholder={t.b2b.form.messagePlaceholder} value={form.message} onChange={set("message")} />
            </div>
            <button
              type="submit"
              data-testid="b2b-submit-btn"
              disabled={status === "loading"}
              className="pill-btn mt-7 flex w-full items-center justify-center gap-2 rounded-full py-4 text-sm font-medium text-white disabled:opacity-60"
              style={{ background: "var(--camel)" }}
            >
              {status === "loading" && <Loader2 size={16} className="animate-spin" />}
              {status === "loading" ? t.b2b.form.sending : t.b2b.form.submit}
            </button>
            {feedback && (
              <p
                data-testid="b2b-form-feedback"
                className="mt-5 rounded-2xl border px-5 py-3.5 text-sm text-center"
                style={{
                  borderColor: status === "success" ? "var(--gold)" : "var(--rose)",
                  background: status === "success" ? "var(--surface-secondary)" : "var(--rose-soft)",
                }}
              >
                {feedback}
              </p>
            )}
          </form>
        </Reveal>
      </div>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 mt-16 sm:mt-20" data-testid="b2b-mirror-features">
        <Reveal>
          <h3 className="font-serif-display text-2xl sm:text-3xl font-semibold">{t.b2b.mirror.title}</h3>
          <p className="mt-2.5 max-w-2xl text-sm sm:text-base" style={{ color: "var(--muted-warm)" }}>{t.b2b.mirror.sub}</p>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {t.b2b.mirror.items.map((item, i) => {
            const Icon = MIRROR_ICONS[i];
            return (
              <Reveal
                key={i}
                delay={(i % 4) * 0.06}
                className="rounded-2xl border p-6 transition-shadow duration-500 hover:shadow-[0_18px_36px_-20px_rgba(198,156,109,0.4)]"
                style={{ background: "var(--surface)", borderColor: "var(--line)" }}
                data-testid={`b2b-mirror-card-${i + 1}`}
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: "var(--rose-soft)" }}>
                  <Icon size={18} strokeWidth={1.7} style={{ color: "var(--camel)" }} />
                </span>
                <h4 className="mt-4 text-sm font-semibold">{item.t}</h4>
                <p className="mt-1.5 text-[13px] leading-relaxed" style={{ color: "var(--muted-warm)" }}>{item.d}</p>
              </Reveal>
            );
          })}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 mt-16 sm:mt-20" data-testid="b2b-mirror-gallery">
        <Reveal>
          <span className="rounded-full border px-3 py-1 text-[10px] font-bold uppercase" style={{ borderColor: "var(--gold)", color: "var(--camel)", letterSpacing: "0.1em" }}>
            {t.b2b.mirrorViz.badge}
          </span>
          <h3 className="mt-4 font-serif-display text-2xl sm:text-3xl font-semibold">{t.b2b.mirrorViz.title}</h3>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed" style={{ color: "var(--muted-warm)" }}>{t.b2b.mirrorViz.sub}</p>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
          {[
            ["/business-mockups/mirror-look.webp", 0],
            ["/business-mockups/mirror-boutique.webp", 1],
            ["/business-mockups/mirror-detail.webp", 2],
            ["/business-mockups/mirror-beratung.webp", 3],
          ].map(([src, i]) => (
            <Reveal key={i} delay={i * 0.08} className="group overflow-hidden rounded-3xl border" style={{ borderColor: "var(--line)", background: "var(--surface)" }} data-testid={`b2b-mirror-img-${i + 1}`}>
              <div className="overflow-hidden">
                <img src={src} alt={t.b2b.mirrorViz.captions[i]} loading="lazy" className="w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <p className="px-6 py-4 text-sm font-medium">{t.b2b.mirrorViz.captions[i]}</p>
            </Reveal>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 mt-16 sm:mt-20" data-testid="b2b-hardware">
        <Reveal>
          <h3 className="font-serif-display text-2xl sm:text-3xl font-semibold">{t.b2b.hardware.title}</h3>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
          {t.b2b.hardware.items.map((hw, i) => {
            const Icon = HW_ICONS[i];
            return (
              <Reveal
                key={i}
                delay={i * 0.1}
                className="group overflow-hidden rounded-3xl border transition-shadow duration-500 hover:shadow-[0_24px_48px_-24px_rgba(198,156,109,0.4)]"
                style={{ background: "var(--surface)", borderColor: "var(--line)" }}
                data-testid={`b2b-hardware-card-${i + 1}`}
              >
                <div className="h-44 overflow-hidden">
                  <img
                    src={HW_IMAGES[i]}
                    alt={hw.name}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-7">
                <div className="flex items-center justify-between">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl transition-transform duration-500 group-hover:-translate-y-1" style={{ background: "var(--rose-soft)" }}>
                    <Icon size={22} strokeWidth={1.6} style={{ color: "var(--camel)" }} />
                  </span>
                  <span className="rounded-full border px-3 py-1 text-[10px] font-bold uppercase" style={{ borderColor: "var(--gold)", color: "var(--camel)", letterSpacing: "0.1em" }}>
                    {hw.plan}
                  </span>
                </div>
                <h4 className="mt-5 font-serif-display text-xl font-semibold leading-snug">{hw.name}</h4>
                <p className="mt-2.5 text-sm leading-relaxed" style={{ color: "var(--muted-warm)" }}>{hw.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        {t.pricing.photoPrivacy && (
          <div className="mt-10 rounded-3xl border p-7 sm:p-8" style={{ borderColor: "var(--line)", background: "var(--surface-secondary)" }} data-testid="b2b-photo-privacy">
            <div className="flex items-center gap-2.5">
              <ShieldCheck size={20} strokeWidth={1.6} style={{ color: "var(--camel)" }} />
              <h4 className="font-serif-display text-xl font-semibold">{t.pricing.photoPrivacy.title}</h4>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              {t.pricing.photoPrivacy.points.map((p, i) => (
                <p key={i} className="text-sm leading-relaxed" style={{ color: "var(--muted-warm)" }} data-testid={`b2b-photo-privacy-point-${i + 1}`}>
                  <span className="font-semibold" style={{ color: "var(--ink, inherit)" }}>{p.split(":")[0]}:</span>
                  {p.substring(p.indexOf(":") + 1)}
                </p>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
